import type { MouseEvent, ReactElement } from 'react';
import { useEffect, useState } from 'react';

import observer from 'saddlebag-observer';

import type { Props as LoginButtonProps } from './LoginButton';

type Props = {
  userLoggedIn: Promise<boolean> | Promise<undefined>;
  onLoginClick: (e: MouseEvent<HTMLButtonElement>, isLoggedIn: boolean) => void;
  loginText: string;
  accountText: string;
  getProfilePicture: () => string | null;
  loginButtonVariant?: string;
};

const enhance = (WrappedComponent: (props: LoginButtonProps) => ReactElement) => {
  const LoginButtonContainer = (props: Props) => {
    const {
      accountText,
      getProfilePicture,
      loginButtonVariant,
      loginText,
      onLoginClick,
      userLoggedIn,
    } = props;

    const [isLoggedIn, setIsLoggedIn] = useState<boolean | undefined>(undefined);
    const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
    const [userLoggedInState, setUserLoggedInState] = useState(userLoggedIn);
    const [loginSuccessSubscribeHandle, setLoginSuccessSubscribeHandle] = useState();

    useEffect(() => {
      if (!loginSuccessSubscribeHandle) {
        // TODO: WOODPECKER-4189: Suppressed for owners to look at while upgrading to eslint-plugin-react-hooks v7

        setLoginSuccessSubscribeHandle(
          observer.subscribe('sid-login-success', () => {
            // TODO: WOODPECKER-4189: Suppressed for owners to look at while upgrading to eslint-plugin-react-hooks v7

            setUserLoggedInState(Promise.resolve(true));
          }),
        );
      }
      return () => {
        observer.unsubscribe(loginSuccessSubscribeHandle);
      };
    }, [loginSuccessSubscribeHandle, setLoginSuccessSubscribeHandle]);

    useEffect(() => {
      userLoggedInState.then((isLoggedInNow) => {
        setIsLoggedIn(isLoggedInNow);
        setProfilePictureUrl(getProfilePicture());
      });
    }, [getProfilePicture, userLoggedInState]);

    const handleProfilePictureError = () => {
      setProfilePictureUrl(null);
    };

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      if (isLoggedIn !== undefined) {
        onLoginClick(e, isLoggedIn);
      }
    };

    return (
      <WrappedComponent
        loginText={loginText}
        accountText={accountText}
        profilePictureUrl={profilePictureUrl}
        isLoggedIn={isLoggedIn}
        onProfilePictureError={handleProfilePictureError}
        onLoginClick={handleClick}
        loginButtonVariant={loginButtonVariant}
      />
    );
  };

  return LoginButtonContainer;
};

export default enhance;
